import _ from 'lodash';
import { useState, useCallback, useRef } from 'react';

import usePromiseQuery from 'core/helpers/usePromiseQuery';
import { useStableCallback } from 'core/hooks/useStableCallback';

import {
  TAbstractEntity,
  TAbstractNewEntity,
  TEditorConfig,
  TEditorOnChange,
} from './interface';

type TArgs<
  TContainer extends TAbstractEntity | null,
  TEntity extends TAbstractEntity,
  TNewEntity extends TAbstractNewEntity,
> = {
  container: TContainer;
  entity: TNewEntity | TEntity;
  mode: 'create' | 'change';
  onResult: (entity: TEntity | null) => void;
  config: TEditorConfig<TContainer, TEntity, TNewEntity>;
};

export default function useEditor<
  TContainer extends TAbstractEntity | null,
  TEntity extends TAbstractEntity,
  TNewEntity extends TAbstractNewEntity,
>({
  container,
  mode,
  entity,
  onResult,
  config,
}: TArgs<TContainer, TEntity, TNewEntity>) {
  const [editingEntity, setEditingEntity] = useState<TEntity | TNewEntity>(
    entity
  );

  const onChange: TEditorOnChange = useStableCallback(evt => {
    const { value } = 'currentTarget' in evt ? evt.target : evt;
    const name =
      'currentTarget' in evt
        ? evt.currentTarget.getAttribute('name')
        : evt.name;
    const [field, subField] = orDie(name)
      // question | hint | options.\d+
      .split('.');

    setEditingEntity(entity =>
      config.transform(entity, field, subField, value)
    );
  });

  const refEntity = useRef(editingEntity);
  refEntity.current = editingEntity;

  const [onSave, isFetching] = usePromiseQuery(
    useCallback(
      async (event?: React.FormEvent): Promise<void> => {
        if (event) event.preventDefault();

        const editingEntity = refEntity.current;

        if (mode === 'change') {
          if (
            !isSavedEntity<TEntity, TNewEntity>(entity) ||
            !isSavedEntity<TEntity, TNewEntity>(editingEntity)
          ) {
            throw new Error('incorrect save-entity mode');
          }

          if (_.isEqual(entity, editingEntity)) {
            onResult(entity); // no changes = no API queries
            return;
          }

          await config.apiUpdateEntity(entity.uid, editingEntity);
          onResult(await config.apiGetEntity(entity.uid));
        } else {
          // create a new entity
          const newEntityUid = await config.apiAddEntity(
            container
              ? container.uid
              : // It's too messy to handle it with TS
                '%fakeParentId%',
            editingEntity
          );
          onResult(await config.apiGetEntity(newEntityUid));
        }
      },
      [entity, container]
    )
  );

  const onCancel = useStableCallback(() => {
    onResult(null);
  });

  return {
    editingEntity,
    onChange,
    onSave,
    onCancel,
    isFetching,
  };
}

const isSavedEntity = <
  TEntity extends TAbstractEntity,
  TNewEntity extends TAbstractNewEntity,
>(
  entity: TEntity | TNewEntity
): entity is TEntity => 'uid' in entity;
